import { Injectable, inject } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { UserRole, UserProfile, AuthService } from './auth.service';
import { AppUser, AppRole, createAppUserFromToken } from '../models/permissions.model';
import { AuthorizationService } from './authorization.service';

/**
 * Legacy mock user interface (kept for backward compatibility)
 * @deprecated Use AppUser from permissions.model instead
 */
export interface MockUser {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'backoffice_admin' | 'backoffice_professional' | 'backoffice_supplier' | 'professional' | 'collaborator';
  trusteeId?: string;
  trusteeType?: 'AUTO_EXPERT' | 'RE_EXPERT' | 'INVESTIGATOR' | 'DOCTOR' | 'LAWYER' | 'BODYSHOP';
  tipologiaFiduciario?:
    | 'AUTO_EXPERT'
    | 'RE_EXPERT'
    | 'INVESTIGATOR'
    | 'DOCTOR'
    | 'LAWYER'
    | 'BODYSHOP'
    | 'HAIL_PROVIDER'
    | 'NETWORK';
  tipologiaCarrozzeria?: 'STANDARD' | 'ARS' | 'RCI' | 'AXA_DEUTSCHE_BANK';
}

/**
 * Mock user profile with Azure AD compatible roles
 * @deprecated Use AppUser from permissions.model instead
 */
export interface MockUserProfile extends UserProfile {
  trusteeId?: string;
  trusteeType?: string;
  tipologiaFiduciario?: string;
  tipologiaCarrozzeria?: string;
}

/**
 * Mock Authentication Service for local development
 * 
 * NOW INTEGRATED WITH:
 * - AppUser (new permissions model)
 * - AuthorizationService (centralized authorization)
 * 
 * This service handles authentication (who you are).
 * AuthorizationService handles authorization (what you can do).
 * 
 * @example
 * ```typescript
 * // In login component (development only)
 * this.mockAuthService.loginAs('backoffice_admin');
 * 
 * // Check user (NEW way)
 * const user = this.mockAuthService.getCurrentUser(); // Returns AppUser
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class UserSessionService {
  private authzService = inject(AuthorizationService);
  private authService = inject(AuthService);

  /**
   * Login as a specific role (development only)
   * NOW: Also sets the user in AuthorizationService
   */
  loginAs(role: UserRole): void {
    const mockData = this.getMockDataForRole(role);
    
    // Store token in-memory via AuthService (never in localStorage)
    this.authService.setToken(mockData.token);

    // Persist mock token in sessionStorage so that _initializeSession() can
    // restore it on F5 without hitting the real BFF (useMockAuth mode only).
    sessionStorage.setItem('mock_access_token', mockData.token);
    
    // ✅ Clear professional_id from sessionStorage
    // User must complete pre-registration to get a professionalId
    sessionStorage.removeItem('professional_id');
    sessionStorage.removeItem('professional_profile');
    
    // Create AppUser and set in AuthorizationService (NEW)
    const appUser = createAppUserFromToken(mockData.profile);
    this.authzService.setUser(appUser);
    
    console.log(`🎭 Mock login as ${role}:`, {
      name: appUser.name,
      roles: appUser.roles,
      permissions: appUser.permissions.length + ' permissions',
    });
  }

  /**
   * Login as a user WITHOUT roles (to test role verification flow)
   * This simulates what happens when a user authenticates but has no roles assigned.
   * The system should:
   * 1. Detect missing roles
   * 2. Attempt automatic role assignment (will fail with 404 if endpoint not implemented)
   * 3. Logout user automatically
   * 4. Show "Nessun ruolo assegnato" message
   * 
   * @example
   * ```typescript
   * // In auth-test component (development only)
   * this.mockAuthService.loginAsUserWithoutRoles();
   * ```
   */
  loginAsUserWithoutRoles(): void {
    const profile: MockUserProfile = {
      id: 'no-roles-user-001',
      email: 'noroles@example.com',
      name: 'Test User Without Roles',
      roles: [],  // ⚠️ Empty roles array - will trigger verification flow
      exp: this.getFutureExpiration(),
    };

    const token = this.createMockToken(profile);
    
    // Store token in-memory via AuthService (never in localStorage)
    this.authService.setToken(token);
    
    // Clear professional_id
    sessionStorage.removeItem('professional_id');
    sessionStorage.removeItem('professional_profile');
    
    // Create AppUser with no roles
    const appUser = createAppUserFromToken(profile);
    this.authzService.setUser(appUser);
    
    console.log('🎭 Mock login as user WITHOUT roles:', {
      name: appUser.name,
      roles: appUser.roles,  // Should be []
      note: 'This will trigger role verification and logout',
    });
  }

  /**
   * Set a real Azure AD token (from PowerShell script or other source)
   * The token is NOT mocked - it's treated as a real authentication token
   * 
   * @param token - Real JWT token string from Azure AD
   * @returns true if token is valid and saved, false otherwise
   */
  setRealToken(token: string): boolean {
    try {
      // Validate JWT format (3 parts)
      const parts = token.trim().split('.');
      if (parts.length !== 3) {
        console.error('❌ Invalid JWT format');
        return false;
      }

      // Decode and validate payload
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      
      // Check expiration
      if (payload.exp) {
        const now = Math.floor(Date.now() / 1000);
        if (payload.exp < now) {
          console.error('❌ Token expired');
          return false;
        }
      }

      // Store token in-memory via AuthService (never in localStorage)
      this.authService.setToken(token.trim());
      
      const profile = {
        id: payload.sub || payload.oid || '',
        email: payload.email || payload.preferred_username || '',
        name: payload.name || payload.preferred_username || '',
        roles: payload.roles || [],
        professionalId: payload.professionalId,
        exp: payload.exp,
      };
      
      console.log('✅ Real token authenticated:', profile.email);
      
      return true;
    } catch (error) {
      console.error('❌ Error setting real token:', error);
      return false;
    }
  }

  /**
   * Get current user (NEW METHOD - returns AppUser)
   * This is what components should use for authorization checks
   */
  getCurrentUser(): AppUser | null {
    return this.authzService.getUser();
  }

  /**
   * Get available roles for mock login
   */
  getAvailableRoles(): UserRole[] {
    return [
      'admin',
      'backoffice_admin',
      'backoffice_professional',
      'backoffice_supplier',
      'professional',
      'collaborator',
    ];
  }

  /**
   * Get current user profile (legacy - for backward compatibility)
   * @deprecated Use getCurrentUser() instead for new code
   */
  getCurrentUserProfile(): MockUserProfile | null {
    const profileStr = sessionStorage.getItem('user_profile');
    if (!profileStr) {
      return null;
    }

    try {
      return JSON.parse(profileStr);
    } catch {
      return null;
    }
  }

getCurrentTrusteeType(): MockUserProfile | null {
    const profileStr = sessionStorage.getItem('user_profile');
    if (!profileStr) {
      return null;
    }

    try {
      return JSON.parse(profileStr);
    } catch {
      return null;
    }
  }


  /**
   * Clear authentication data
   * NOW: Also clears AuthorizationService
   */
  clearAuth(): void {
    // Clear in-memory token via AuthService (does NOT navigate)
    this.authService.clearTokenOnly();
    
    // Remove legacy localStorage entries (cleanup for older browsers)
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    localStorage.removeItem('user_profile');
    localStorage.removeItem('auth_return_url');
    
    // Clear sessionStorage
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('user_profile');
    sessionStorage.removeItem('professional_id');
    sessionStorage.removeItem('professional_profile');
    sessionStorage.removeItem('auth_state');
    sessionStorage.removeItem('auth_code_verifier');
    
    // Clear AuthorizationService
    this.authzService.clearUser();
    
    console.log('🗑️ [MockAuth] All auth data cleared');
  }

  /**
   * Save demo professional ID for simulation
   */
  setDemoProfessionalId(professionalId: string): void {
    if (professionalId && professionalId.trim()) {
      sessionStorage.setItem('professional_id', professionalId.trim());
      console.log(`✅ [MockAuthService] Professional ID salvato: ${professionalId.trim()}`);
    } else {
      console.log('⚠️ [MockAuthService] Nessun Professional ID fornito, verrà rimosso dalla sessione');
      sessionStorage.removeItem('professional_id');
    }
  }

  /**
   * Get professional ID from session
   */
  getDemoProfessionalId(): string | null {
    const id = sessionStorage.getItem('professional_id');
    if (id) {
      console.log(`✅ [MockAuthService] Professional ID recuperato dalla sessione: ${id}`);
    } else {
      console.log('⚠️ [MockAuthService] Nessun Professional ID trovato in sessione');
    }
    return id;
  }

  /**
   * Clear demo professional ID
   */
  clearDemoProfessionalId(): void {
    sessionStorage.removeItem('professional_id');
    console.log('🗑️ [MockAuthService] Professional ID rimosso dalla sessione');
  }

  /**
   * Simulate getAuthProfile API call for mock authentication
   * Returns mock professional profile or NOT_FOUND error based on professional ID presence
   */
  simulateGetAuthProfile(): Observable<any> {
    const professionalId = this.getDemoProfessionalId();
    
    if (professionalId && professionalId.trim()) {
      // Simulate successful profile fetch
      const user = this.getCurrentUser();
      const mockProfile = {
        professionalId: professionalId,
        type: user?.trusteeType || 'DOCTOR',
        firstname: user?.name?.split(' ')[0] || 'John',
        lastname: user?.name?.split(' ')[1] || 'Doe',
        fiscalCode: 'RSSMRA80A01H501X',
        registrationEmail: user?.email || 'professional@example.com',
        status: 'ACTIVE',
        role: user?.roles.includes(AppRole.PROFESSIONAL) ? 'PROFESSIONAL' : 'COLLABORATOR',
        isCollaborator: user?.roles.includes(AppRole.COLLABORATOR) || false,
      };
      
      console.log('✅ [MockAuthService] Simulated profile fetch:', mockProfile);
      return of(mockProfile);
    } else {
      // Simulate NOT_FOUND error
      const error = {
        error: {
          success: false,
          message: 'No professional profile found for the authenticated user',
          errorCode: 'NOT_FOUND',
          timestamp: new Date().toISOString(),
        },
        status: 404,
      };
      
      console.log('⚠️ [MockAuthService] Simulated NOT_FOUND error');
      return throwError(() => error);
    }
  }

  // ==================== Private Methods ====================

  /**
   * Get mock data for a specific role
   */
  private getMockDataForRole(role: UserRole): { token: string; profile: MockUserProfile } {
    const roleConfigs: Record<UserRole, Omit<MockUserProfile, 'exp'>> = {
      admin: {
        id: 'super-admin-001',
        email: 'superadmin@axa.com',
        name: 'Super Admin',
        roles: ['admin'],
      },
      backoffice_admin: {
        id: 'admin-001',
        email: 'admin@axa.com',
        name: 'Admin User',
        roles: ['backoffice_admin'],
      },
      backoffice_professional: {
        id: 'prof-001',
        email: 'professionals@axa.com',
        name: 'Professionals Manager',
        roles: ['backoffice_professional'],
      },
      backoffice_supplier: {
        id: 'supp-001',
        email: 'supplier@axa.com',
        name: 'Supplier Manager',
        roles: ['backoffice_supplier'],
      },
      professional: {
        id: 'fp-001',
        email: 'professional@example.com',
        name: 'John Professional',
        roles: ['professional'],
        // professionalId is NOT set by default - user must complete pre-registration first
        trusteeType: 'AUTO_EXPERT',
        tipologiaFiduciario: 'AUTO_EXPERT',
      },
      collaborator: {
        id: 'coll-001',
        email: 'collaborator@example.com',
        name: 'Jane Collaborator',
        roles: ['collaborator'],
        // professionalId is NOT set by default - must be associated with a professional
        trusteeType: 'RE_EXPERT',
        tipologiaFiduciario: 'RE_EXPERT',
      },
    };

    const config = roleConfigs[role];
    const profile: MockUserProfile = {
      ...config,
      exp: this.getFutureExpiration(),
    };

    return {
      token: this.createMockToken(profile),
      profile,
    };
  }

  /**
   * Creates a mock JWT token with the given payload
   */
  private createMockToken(payload: any): string {
    const header = { alg: 'HS256', typ: 'JWT' };
    const fullPayload = {
      ...payload,
      iat: Math.floor(Date.now() / 1000),
      exp: this.getFutureExpiration(),
    };

    const encodedHeader = btoa(JSON.stringify(header));
    const encodedPayload = btoa(JSON.stringify(fullPayload));
    const signature = 'mock_signature';

    return `${encodedHeader}.${encodedPayload}.${signature}`;
  }

  /**
   * Gets future expiration timestamp (1 year from now)
   */
  private getFutureExpiration(): number {
    return Math.floor(Date.now() / 1000) + 365 * 24 * 60 * 60;
  }

  // ==================== Legacy Methods ====================

  /**
   * Legacy method - Load mock JWT token for backward compatibility
   * @deprecated This method has been removed for security reasons. Use loginAs() with new roles instead.
   */
  loadMockToken(role: 'admin' | 'supplierAdmin' | 'expertAdmin' | 'expert'): void {
    console.warn('⚠️ loadMockToken() is deprecated and has been removed for security reasons.');
    console.warn('Use loginAs() instead. Example: mockAuthService.loginAs("backoffice_admin")');
    console.warn('Available roles: backoffice_admin, backoffice_manager, professional, collaborator, expert');
  }

  // ==================== LEGACY METHODS (Keep for backward compatibility) ====================

  /**
   * Get current user from sessionStorage (legacy)
   * @deprecated Use getCurrentUser() which returns AppUser instead
   */
  getLegacyUser(): MockUser | null {
    const userStr = sessionStorage.getItem('user');
    if (!userStr) {
      return null;
    }

    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  }
}
